// LeadLog.jsx

import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useUserAuth } from "../../context/UserAuthContext";
import { STAGE_OPTIONS } from "./stageOptions";
import { Modal } from "react-bootstrap";

import {
  FaPhoneAlt,
  FaEnvelope,
  FaCalendarAlt,
  FaWhatsapp,
  FaExchangeAlt,
  FaPlus,
  FaStickyNote,
  FaTag,
  FaCheck,
  FaLink,
  FaClipboardList,
  FaArrowLeft,
  FaEdit,
  FaUserEdit,
  FaMapMarkerAlt,
  FaPercent,
  FaCircle,
  FaClock,
  FaDownload,
  FaFilePdf,
  FaFileInvoice,
  FaGem,
  FaStar,
  FaEye,
  FaArrowRight,
  FaSpinner,
} from "react-icons/fa";

import DestinationWeddingQuotationPdf from "../quotation/DestinationWeddingQuotationPdf";
import InternationalWeddingQuotationPdf from "../quotation/InternationalWeddingQuotationPdf";

import WeddingQuotationPDF from "../quotation/WeddingQuotationPDF";
import BirthdayQuotation from "../quotation/BirthdayQuotation";
import CorporateQuotation from "../quotation/CorporateQuotation";
import AnniversaryQuotation from "../quotation/AnniversaryQuotation";

/* ─────────────────────────────────────────────
   INLINE STYLES  (no external CSS dependency)
───────────────────────────────────────────── */
const S = {
  /* page */
  page: {
    minHeight: "100vh",
    background: "#f0f4f9",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    padding: "24px 20px 48px",
  },
  inner: { margin: "0 auto" },

  /* ── HEADER CARD ── */
  headerCard: {
    background: "linear-gradient(135deg, #0f4c8a 0%, #051e3e 100%)",
    borderRadius: 20,
    padding: "28px 28px 24px",
    marginBottom: 20,
    position: "relative",
    overflow: "hidden",
  },
  headerGlow: {
    position: "absolute",
    top: -80,
    right: -80,
    width: 260,
    height: 260,
    borderRadius: "50%",
    background: "rgba(99,179,237,0.12)",
    pointerEvents: "none",
  },
  headerGlow2: {
    position: "absolute",
    bottom: -50,
    left: 60,
    width: 160,
    height: 160,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.04)",
    pointerEvents: "none",
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 16,
    position: "relative",
    zIndex: 1,
  },
  leadName: {
    fontSize: 26,
    fontWeight: 700,
    color: "#fff",
    marginBottom: 12,
    letterSpacing: "-0.3px",
  },
  badgeRow: { display: "flex", flexWrap: "wrap", gap: 7 },

  /* badges */
  badge: (color) => {
    const map = {
      default: {
        bg: "rgba(255,255,255,0.12)",
        text: "#dce9f5",
        border: "rgba(255,255,255,0.2)",
      },
      warn: {
        bg: "rgba(250,199,117,0.18)",
        text: "#FAC775",
        border: "rgba(250,199,117,0.35)",
      },
      success: {
        bg: "rgba(29,158,117,0.2)",
        text: "#5DCAA5",
        border: "rgba(29,158,117,0.4)",
      },
      info: {
        bg: "rgba(99,179,237,0.2)",
        text: "#93C5FD",
        border: "rgba(99,179,237,0.4)",
      },
      dark: {
        bg: "rgba(0,0,0,0.3)",
        text: "#adb5bd",
        border: "rgba(255,255,255,0.15)",
      },
      gold: {
        bg: "rgba(212,168,83,0.25)",
        text: "#f0d78c",
        border: "rgba(212,168,83,0.4)",
      },
    };
    const c = map[color] || map.default;
    return {
      fontSize: 12,
      fontWeight: 500,
      padding: "4px 11px",
      borderRadius: 20,
      background: c.bg,
      color: c.text,
      border: `0.5px solid ${c.border}`,
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
    };
  },

  /* header buttons */
  btnRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
    position: "relative",
    zIndex: 1,
  },
  btn: (variant) => {
    const styles = {
      primary: {
        background: "rgba(255,255,255,0.92)",
        color: "#051e3e",
        border: "none",
      },
      warning: {
        background: "rgba(250,199,117,0.2)",
        color: "#FAC775",
        border: "0.5px solid rgba(250,199,117,0.4)",
      },
      ghost: {
        background: "rgba(255,255,255,0.1)",
        color: "#fff",
        border: "0.5px solid rgba(255,255,255,0.25)",
      },
      success: {
        background: "rgba(16,185,129,0.2)",
        color: "#6ee7b7",
        border: "0.5px solid rgba(16,185,129,0.4)",
      },
      info: {
        background: "rgba(99,102,241,0.2)",
        color: "#a5b4fc",
        border: "0.5px solid rgba(99,102,241,0.4)",
      },
      gold: {
        background: "rgba(212,168,83,0.2)",
        color: "#f0d78c",
        border: "0.5px solid rgba(212,168,83,0.4)",
      },
    };
    const s = styles[variant] || styles.ghost;
    return {
      ...s,
      fontSize: 12,
      fontWeight: 600,
      padding: "8px 16px",
      borderRadius: 10,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      transition: "all .15s",
      textDecoration: "none",
    };
  },

  /* Quotation buttons section */
  quotationSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTop: "0.5px solid rgba(255,255,255,0.15)",
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
    position: "relative",
    zIndex: 1,
  },
  quotationLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "rgba(255,255,255,0.5)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginRight: 4,
  },

  /* ── CARD SHELL ── */
  card: {
    background: "#fff",
    borderRadius: 18,
    border: "0.5px solid #e2e8f0",
    marginBottom: 16,
    overflow: "hidden",
  },
  cardHeader: {
    padding: "16px 22px 14px",
    borderBottom: "0.5px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#1a202c",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  cardTitleIcon: {
    width: 28,
    height: 28,
    borderRadius: 8,
    background: "#EBF4FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    color: "#2563EB",
  },

  /* ── STAGE TIMELINE ── */
  stageWrap: {
    padding: "22px 20px 20px",
    overflowX: "auto",
  },
  stageTrack: {
    display: "flex",
    alignItems: "flex-start",
    minWidth: 680,
    position: "relative",
  },
  stageStep: {
    flex: 1,
    minWidth: 80,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
  },
  connector: (done) => ({
    position: "absolute",
    top: 19,
    left: "50%",
    width: "100%",
    height: 3,
    borderRadius: 4,
    background: done ? "#1D9E75" : "#e2e8f0",
    zIndex: 0,
    transition: "background .3s",
  }),
  dot: (type) => {
    const map = {
      completed: { bg: "#1D9E75", color: "#fff", shadow: "0 0 0 3px #d1fae5" },
      current: { bg: "#2563EB", color: "#fff", shadow: "0 0 0 3px #dbeafe" },
      future: { bg: "#f1f5f9", color: "#94a3b8", shadow: "0 0 0 2px #e2e8f0" },
      dead: { bg: "#1e293b", color: "#94a3b8", shadow: "0 0 0 2px #334155" },
    };
    const d = map[type] || map.future;
    return {
      width: 40,
      height: 40,
      borderRadius: "50%",
      background: d.bg,
      color: d.color,
      boxShadow: d.shadow,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 600,
      position: "relative",
      zIndex: 2,
      border: "3px solid #fff",
      cursor: "default",
      transition: "transform .2s",
    };
  },
  nowPill: {
    marginTop: 4,
    fontSize: 10,
    fontWeight: 600,
    background: "#dbeafe",
    color: "#1d4ed8",
    padding: "2px 8px",
    borderRadius: 20,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  stageLabel: (future) => ({
    marginTop: 8,
    fontSize: 11,
    fontWeight: 500,
    textAlign: "center",
    lineHeight: 1.4,
    color: future ? "#94a3b8" : "#374151",
    maxWidth: 78,
    padding: "0 2px",
  }),

  /* ── LOG FEED ── */
  logFeed: { position: "relative" },
  spine: {
    position: "absolute",
    left: 48,
    top: 0,
    bottom: 0,
    width: 1.5,
    background: "#e2e8f0",
    zIndex: 0,
  },
  logItem: {
    display: "flex",
    gap: 14,
    padding: "16px 22px",
    position: "relative",
    transition: "background .12s",
    cursor: "default",
  },
  logIconWrap: (color) => {
    const map = {
      blue: { bg: "#EBF4FF", text: "#2563EB" },
      green: { bg: "#ECFDF5", text: "#065F46" },
      amber: { bg: "#FFFBEB", text: "#92400E" },
      gray: { bg: "#F1F5F9", text: "#475569" },
      red: { bg: "#FEF2F2", text: "#B91C1C" },
    };
    const c = map[color] || map.gray;
    return {
      width: 30,
      height: 30,
      borderRadius: "50%",
      background: c.bg,
      color: c.text,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 14,
      position: "relative",
      zIndex: 1,
      marginTop: 2,
      border: `1.5px solid ${c.bg}`,
    };
  },
  logBody: { flex: 1, minWidth: 0 },
  logMeta: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 3,
    flexWrap: "wrap",
  },
  logAction: { fontSize: 13, fontWeight: 600, color: "#111827" },
  logTime: { fontSize: 11, color: "#9ca3af" },
  logMsg: { fontSize: 13, color: "#4b5563", lineHeight: 1.65 },
  logTag: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 500,
    background: "#f8fafc",
    color: "#64748b",
    border: "0.5px solid #e2e8f0",
    padding: "2px 9px",
    borderRadius: 20,
    marginTop: 6,
  },

  /* divider between log items */
  logDivider: { borderTop: "0.5px solid #f1f5f9", margin: "0 22px" },

  /* empty state */
  empty: { textAlign: "center", padding: "48px 20px", color: "#94a3b8" },
  emptyIcon: { fontSize: 36, marginBottom: 12 },
  emptyText: { fontSize: 14 },

  /* loader */
  loaderWrap: {
    display: "flex",
    justifyContent: "center",
    padding: "80px 20px",
  },
  spinner: {
    width: 36,
    height: 36,
    border: "3px solid #e2e8f0",
    borderTop: "3px solid #2563EB",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  },

  countBadge: {
    fontSize: 12,
    fontWeight: 500,
    background: "#f1f5f9",
    color: "#64748b",
    padding: "2px 10px",
    borderRadius: 20,
  },
};

/* ─────────────────────────────────────────────
   ICON MAP  (React Icons)
───────────────────────────────────────────── */
const ICONS = {
  phone: <FaPhoneAlt size={12} />,
  email: <FaEnvelope size={12} />,
  calendar: <FaCalendarAlt size={12} />,
  whatsapp: <FaWhatsapp size={12} />,
  stage: <FaExchangeAlt size={12} />,
  created: <FaPlus size={12} />,
  note: <FaStickyNote size={12} />,
  default: <FaTag size={12} />,
  check: <FaCheck size={14} />,
  pipeline: <FaLink size={12} />,
  activity: <FaClipboardList size={12} />,
};

/* Helper: derive icon + color from log message */
function getLogMeta(message = "") {
  const m = message.toLowerCase();
  if (m.includes("call") || m.includes("phone"))
    return { icon: ICONS.phone, color: "blue" };
  if (m.includes("email") || m.includes("mail"))
    return { icon: ICONS.email, color: "blue" };
  if (m.includes("whatsapp")) return { icon: ICONS.whatsapp, color: "green" };
  if (m.includes("visit") || m.includes("meet"))
    return { icon: ICONS.calendar, color: "amber" };
  if (m.includes("stage") || m.includes("moved"))
    return { icon: ICONS.stage, color: "amber" };
  if (m.includes("created") || m.includes("add"))
    return { icon: ICONS.created, color: "gray" };
  if (m.includes("proposal") || m.includes("quot"))
    return { icon: ICONS.note, color: "green" };
  return { icon: ICONS.default, color: "gray" };
}

/* ─────────────────────────────────────────────
   LEAD TYPE CONFIG
───────────────────────────────────────────── */
const QUOTATION_TYPES = [
  "wedding_quotation",
  "birthday_quotation",
  "anniversary_quotation",
  "corporate_quotations",
  "destination_wedding_quotation",
  "international_wedding_quotation",
];

const getQuotationRoute = (leadType) => {
  switch (leadType) {
    case "wedding_quotation":
      return "wedding-client-quotation";
    case "birthday_quotation":
      return "birthday-client-quotation";
    case "anniversary_quotation":
      return "anniversary-client-quotation";
    case "corporate_quotations":
      return "corporate-client-quotation";
    case "destination_wedding_quotation":
      return "destination-client-quotation";
    case "international_wedding_quotation":
      return "international-client-quotation";
    default:
      return null;
  }
};

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
const LeadLog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useUserAuth();

  const base_url = import.meta.env.VITE_PUBLIC_BASE_URL_DEVELOPMENT;

  const [loading, setLoading] = useState(false);
  const [lead, setLead] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stage, setStage] = useState([]);

  // PDF/Deal modal states
  const [myPdf, setMyPdf] = useState(null);
  const [clientQuotationData, setClientQuotationData] = useState(null);
  const [loadingClientPdf, setLoadingClientPdf] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [showClientPdf, setShowClientPdf] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);

  // Check if lead is quotation type
  const isQuotationType = useMemo(() => {
    return lead?.lead_type && QUOTATION_TYPES.includes(lead.lead_type);
  }, [lead?.lead_type]);

  // Check if has price info
  const hasPriceInfo =
    lead?.price_info && Object.keys(lead?.price_info).length > 0;
  const hasDealPrice = lead?.price_info?.deal_close_price != null;

  // Get quotation route and ID
  const quotationRoute = lead?.lead_type
    ? getQuotationRoute(lead.lead_type)
    : null;
  const quotationId = lead?.source_id || lead?.quotation_details?.id;

  /* ── fetches ── */
  const getLeadDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${base_url}/api/v1/leads/single/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLead(res?.data?.data);
    } catch (e) {
      console.log("Lead Details Error:", e);
    } finally {
      setLoading(false);
    }
  };

  const getLeadLogs = async () => {
    try {
      const res = await axios.get(`${base_url}/api/v1/leads/logs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(res?.data?.data || []);
    } catch (e) {
      console.log("Lead Logs Error:", e);
    }
  };

  const getLeadStages = async () => {
    try {
      const res = await axios.get(`${base_url}/api/v1/leads/stage/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStage(res?.data?.data || []);
    } catch (e) {
      console.log("Lead Stage Error:", e);
    }
  };

  useEffect(() => {
    getLeadDetails();
    getLeadLogs();
    getLeadStages();
  }, [id]);

  const handleClosePdf = () => setShowPdf(false);

  const pdfHandler = () => {
    setMyPdf(lead);
    setShowPdf(true);
  };

  const renderPdfComponent = () => {
    if (!myPdf) return null;
    switch (myPdf.lead_type) {
      case "wedding_quotation":
        return <WeddingQuotationPDF pdfData={myPdf} />;
      case "destination_wedding_quotation":
        return <DestinationWeddingQuotationPdf pdfData={myPdf} />;
      case "international_wedding_quotation":
        return <InternationalWeddingQuotationPdf pdfData={myPdf} />;
      case "birthday_quotation":
        return <BirthdayQuotation pdfData={myPdf} />;
      case "corporate_quotations":
        return <CorporateQuotation pdfData={myPdf} />;
      case "anniversary_quotation":
        return <AnniversaryQuotation pdfData={myPdf} />;
      default:
        return <div className="text-danger p-3">Unknown quotation type</div>;
    }
  };

  /* ── date format ── */
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return null;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  /* ── stage helpers ── */
  const currentStage = stage.find((s) => s.is_current === true);
  const currentStageIdx = STAGE_OPTIONS.findIndex(
    (s) => s.value === currentStage?.stage_key,
  );
  const isDead = currentStage?.stage_key === "not_converted";
  const isWon =
    currentStage?.stage_key === "converted" || lead?.progress === 100;

  /* ── loader ── */
  if (loading) {
    return (
      <div className="page-wrapper">
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={S.loaderWrap}>
          <div style={S.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .log-item-row:hover { background: #fafbfc !important; }
        .hdr-btn:hover { opacity: 0.85; transform: translateY(-1px); }
        .stage-dot-el:hover { transform: scale(1.12); }
        .qt-btn:hover { opacity: 0.85; transform: translateY(-1px); }
      `}</style>

        <div style={S.page}>
          <div style={S.inner}>
            {/* ── HEADER ── */}
            <div style={S.headerCard}>
              <div style={S.headerGlow} />
              <div style={S.headerGlow2} />

              <div style={S.headerTop}>
                {/* left */}
                <div>
                  <div style={S.leadName}>
                    {lead?.lead_name || lead?.name || "Lead Details"}
                  </div>
                  <div style={S.badgeRow}>
                    {lead?.name && (
                      <span style={S.badge("default")}>
                        <FaUserEdit size={10} /> {lead.name}
                      </span>
                    )}
                    {lead?.mob_no && (
                      <span style={S.badge("default")}>
                        <FaPhoneAlt size={10} /> {lead.mob_no}
                      </span>
                    )}
                    {lead?.whatsapp_no && (
                      <span style={S.badge("default")}>
                        <FaWhatsapp size={10} /> {lead.whatsapp_no}
                      </span>
                    )}
                    {lead?.lead_status && (
                      <span style={S.badge("warn")}>{lead.lead_status}</span>
                    )}
                    {lead?.status && (
                      <span style={S.badge("success")}>{lead.status}</span>
                    )}
                    {lead?.stage_name && (
                      <span style={S.badge("info")}>{lead.stage_name}</span>
                    )}
                    <span style={S.badge("dark")}>
                      <FaPercent size={10} /> {lead?.progress || 0}%
                    </span>
                    {lead?.city_name && (
                      <span style={S.badge("default")}>
                        <FaMapMarkerAlt size={10} /> {lead.city_name}
                      </span>
                    )}
                    {hasDealPrice && (
                      <span style={S.badge("gold")}>
                        <FaGem size={10} />{" "}
                        {formatCurrency(lead.price_info.deal_close_price)}
                      </span>
                    )}
                  </div>
                </div>

                {/* right buttons */}
                <div style={S.btnRow}>
                  <button
                    className="hdr-btn"
                    style={S.btn("primary")}
                    onClick={() =>
                      navigate(`/dashboard/update-lead/${lead?.id}`)
                    }
                  >
                    <FaEdit size={12} /> Update Lead
                  </button>
                  <button
                    className="hdr-btn"
                    style={S.btn("warning")}
                    onClick={() =>
                      navigate(`/dashboard/update-lead-details/${lead?.id}`)
                    }
                  >
                    <FaUserEdit size={12} /> Basic Info
                  </button>
                  <button
                    className="hdr-btn"
                    style={S.btn("ghost")}
                    onClick={() => navigate(-1)}
                  >
                    <FaArrowLeft size={12} /> Back
                  </button>
                </div>
              </div>

              {/* ── QUOTATION BUTTONS SECTION ── */}
              {isQuotationType && (
                <div style={S.quotationSection}>
                  <span style={S.quotationLabel}>
                    <FaFileInvoice size={10} /> Quotation Actions
                  </span>

                  <button
                    className="qt-btn"
                    onClick={pdfHandler}
                    style={S.btn("ghost")}
                  >
                    <FaDownload size={11} /> Download QT
                  </button>

                  {isWon && hasPriceInfo && (
                    <button className="qt-btn" style={S.btn("gold")}>
                      <FaGem size={11} />
                      {hasDealPrice
                        ? `Deal: ${formatCurrency(lead.price_info.deal_close_price)}`
                        : "Set Price"}
                      <FaArrowRight size={10} />
                    </button>
                  )}
                </div>
              )}

              {/* Package Enquiry Deal Button */}
              {lead?.lead_type === "package_enquiry" &&
                hasPriceInfo &&
                isWon && (
                  <div style={S.quotationSection}>
                    <span style={S.quotationLabel}>
                      <FaStar size={10} /> Package Actions
                    </span>
                    <button
                      className="qt-btn"
                      onClick={() => setShowDealModal(true)}
                      style={S.btn("info")}
                    >
                      <FaStar size={11} /> Package Deal
                      <FaArrowRight size={10} />
                    </button>
                  </div>
                )}

              {/* Requests Deal Button */}
              {lead?.lead_type === "requests" && hasPriceInfo && isWon && (
                <div style={S.quotationSection}>
                  <span style={S.quotationLabel}>
                    <FaEye size={10} /> Request Actions
                  </span>
                  <button
                    className="qt-btn"
                    onClick={() => setShowDealModal(true)}
                    style={S.btn("warning")}
                  >
                    <FaEye size={11} /> Request Deal
                    <FaArrowRight size={10} />
                  </button>
                </div>
              )}

              {/* Venue Booking Deal Button */}
              {lead?.lead_type === "venue_bookings" &&
                hasPriceInfo &&
                isWon && (
                  <div style={S.quotationSection}>
                    <span style={S.quotationLabel}>
                      <FaStar size={10} /> Venue Actions
                    </span>
                    <button
                      className="qt-btn"
                      onClick={() => setShowDealModal(true)}
                      style={S.btn("success")}
                    >
                      <FaStar size={11} /> Venue Deal
                      <FaArrowRight size={10} />
                    </button>
                  </div>
                )}
            </div>

            {/* ── STAGE TIMELINE ── */}
            <div style={S.card}>
              <div style={S.cardHeader}>
                <div style={S.cardTitle}>
                  <div style={S.cardTitleIcon}>
                    <FaLink size={14} />
                  </div>
                  Pipeline stages
                </div>
                {currentStage && (
                  <span
                    style={{
                      fontSize: 12,
                      color: "#2563EB",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <FaCircle size={6} /> Current:{" "}
                    {currentStage.stage_key?.replace(/_/g, " ")}
                  </span>
                )}
              </div>

              <div style={S.stageWrap}>
                <div style={S.stageTrack}>
                  {STAGE_OPTIONS.map((item, index) => {
                    const isCompleted = stage.some(
                      (s) =>
                        s.stage_key === item.value && s.is_current === false,
                    );
                    const isCurrent = currentStage?.stage_key === item.value;
                    const isFuture = index > currentStageIdx;
                    const isLast = index === STAGE_OPTIONS.length - 1;

                    let dotType = "future";
                    if (isCompleted) dotType = "completed";
                    if (isCurrent) dotType = isDead ? "dead" : "current";

                    const lineIsDone = index < currentStageIdx && !isDead;

                    return (
                      <div key={item.value} style={S.stageStep}>
                        {!isLast && <div style={S.connector(lineIsDone)} />}
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            zIndex: 2,
                            position: "relative",
                          }}
                        >
                          <div className="stage-dot-el" style={S.dot(dotType)}>
                            {isCompleted ? ICONS.check : item.progress + "%"}
                          </div>
                          {isCurrent && (
                            <span style={S.nowPill}>
                              <FaCircle size={6} /> Now
                            </span>
                          )}
                        </div>
                        <div style={S.stageLabel(isFuture && !isCurrent)}>
                          {item.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── ACTIVITY LOG ── */}
            <div style={S.card}>
              <div style={S.cardHeader}>
                <div style={S.cardTitle}>
                  <div style={S.cardTitleIcon}>
                    <FaClipboardList size={14} />
                  </div>
                  Activity log
                </div>
                <span style={S.countBadge}>{logs.length} events</span>
              </div>

              {logs.length > 0 ? (
                <div style={S.logFeed}>
                  <div style={S.spine} />
                  {logs.map((item, i) => {
                    const { icon, color } = getLogMeta(item.message);
                    return (
                      <React.Fragment key={item.id}>
                        {i > 0 && <div style={S.logDivider} />}
                        <div className="log-item-row" style={S.logItem}>
                          <div style={S.logIconWrap(color)}>{icon}</div>
                          <div style={S.logBody}>
                            <div style={S.logMeta}>
                              <span style={S.logAction}>Activity #{i + 1}</span>
                              <span style={S.logTime}>
                                {formatDate(item.created_at)}
                              </span>
                            </div>
                            <div
                              style={S.logMsg}
                              dangerouslySetInnerHTML={{ __html: item.message }}
                            />
                            <span style={S.logTag}>
                              <FaTag size={10} /> Log
                            </span>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : (
                <div style={S.empty}>
                  <div style={S.emptyIcon}>📭</div>
                  <p style={S.emptyText}>
                    No activity logs found for this lead.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Quotation PDF Modal ── */}
        <Modal show={showPdf} onHide={handleClosePdf} size="lg" centered>
          <Modal.Header
            closeButton
            style={{
              background: "linear-gradient(135deg, #1e293b, #0f172a)",
              color: "#fff",
              borderBottom: "2px solid #334155",
            }}
          >
            <Modal.Title
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <FaDownload size={16} /> Download Quotation PDF
            </Modal.Title>
          </Modal.Header>
          <Modal.Body
            className="p-0"
            style={{ maxHeight: "70vh", overflow: "auto" }}
          >
            {renderPdfComponent()}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default LeadLog;
